import React, { useState, useEffect } from 'react';
import { getTvDashboardData } from '../../lib/treeApi';
import { useTreeAuth } from '../../lib/treeAuth';
import { CheckCircle, AlertTriangle, XCircle, Truck, Package, TreePine, Clock, MapPin, Radio } from 'lucide-react';

import { colourHex, swatchStyle, pillStyle, pillGlow, registerColours } from '../../lib/treeColors';
const WC_LOGO = "https://customer-assets.emergentagent.com/job_tree-logistics/artifacts/7lohxwsd_2025Logo.png";
const PINE_BG = "https://images.unsplash.com/photo-1506452305024-9d3f02d1c9b5?crop=entropy&cs=srgb&fm=jpg&q=85";

const CARD = "bg-[#0E2319]/80 backdrop-blur-xl border border-[#2E7553]/25 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]";

export default function TreeTVDashboard() {
  const { settings } = useTreeAuth();
  const [loads, setLoads] = useState([]);
  const [colours, setColours] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [time, setTime] = useState(new Date());
  const [pulse, setPulse] = useState(false);
  const [stale, setStale] = useState(false);

  const fetchAll = async () => {
    try {
      const { data } = await getTvDashboardData();
      setLoads(data.loads);
      setColours(data.colours);
      registerColours(data.colours);
      setAnalytics(data.analytics);
      setDispatches(data.recent_dispatches || []);
      setPulse(true);
      setStale(false);
      setTimeout(() => setPulse(false), 1200);
    } catch {
      setStale(true);
    }
  };

  useEffect(() => {
    fetchAll();
    const dataInterval = setInterval(fetchAll, 30000);
    const clockInterval = setInterval(() => setTime(new Date()), 1000);
    return () => { clearInterval(dataInterval); clearInterval(clockInterval); };
  }, []);

  const getStock = (color) => colours.find(c => c.color === color?.toLowerCase())?.quantity || 0;
  const labelFor = (color) => colours.find(c => c.color === color?.toLowerCase())?.label || color;
  const totalStock = colours.reduce((s, c) => s + (c.quantity || 0), 0);

  const upcoming = loads
    .filter(l => l.status === 'loading')
    .sort((a, b) => (a.departure_date || '').localeCompare(b.departure_date || ''));

  const now = new Date();
  const in7days = new Date(now.getTime() + 7 * 86400000);
  const next7 = upcoming.filter(l => l.departure_date && new Date(l.departure_date + 'T00:00:00') <= in7days);

  const totalRequired = {};
  next7.forEach(l => (l.pallets_required || []).forEach(p => {
    const c = p.color?.toLowerCase();
    if (c) totalRequired[c] = (totalRequired[c] || 0) + p.quantity;
  }));

  const shortfallByColour = colours.reduce((acc, c) => {
    const needed = totalRequired[c.color] || 0;
    const amount = needed > 0 ? Math.max(0, needed - c.quantity) : 0;
    if (amount > 0) acc.push({ color: c.color, label: c.label || c.color, amount });
    return acc;
  }, []);

  const totalNeeded = Object.values(totalRequired).reduce((s, v) => s + v, 0);
  const totalShortfall = shortfallByColour.reduce((s, x) => s + x.amount, 0);
  const allGood = totalShortfall === 0;

  const fmtDate = (s) => {
    if (!s) return '';
    const d = new Date(s + 'T00:00:00');
    const today = new Date(); today.setHours(0,0,0,0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const dDate = new Date(d); dDate.setHours(0,0,0,0);
    if (dDate.getTime() === today.getTime()) return 'TODAY';
    if (dDate.getTime() === tomorrow.getTime()) return 'TOMORROW';
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }).toUpperCase();
  };

  const timeAgo = (iso) => {
    const diffMs = now.getTime() - new Date(iso).getTime();
    const mins = Math.max(0, Math.floor(diffMs / 60000));
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const tickerItems = dispatches.map((d, i) => {
    const colourStr = (d.pallets_required || []).map(p => `${p.quantity} ${labelFor(p.color)}`).join(', ');
    const dest = d.destinations?.[0]?.address;
    return {
      key: i,
      text: `${d.driver_name || 'Driver'} dispatched${dest ? ` to ${dest}` : ''}${colourStr ? ` — ${colourStr}` : ''} — ${timeAgo(d.signed_at)}`,
    };
  });

  const banner = upcoming.length === 0
    ? { label: 'No Orders', sub: 'No upcoming collections', icon: Package, bg: 'rgba(100,116,139,0.15)', border: '#64748B', text: '#94A3B8', glow: 'none' }
    : totalNeeded === 0
    ? { label: 'Stock OK', sub: 'Nothing due in the next 7 days', icon: CheckCircle, bg: 'rgba(34,197,94,0.15)', border: '#22C55E', text: '#4ADE80', glow: '0 0 28px rgba(34,197,94,0.35)' }
    : allGood
    ? { label: 'Stock OK', sub: 'All collections covered', icon: CheckCircle, bg: 'rgba(34,197,94,0.15)', border: '#22C55E', text: '#4ADE80', glow: '0 0 28px rgba(34,197,94,0.35)' }
    : { label: `Need ${totalShortfall} More`, sub: `${shortfallByColour.map(s => `${s.amount} ${s.label}`).join(', ')} — collect from field`, icon: AlertTriangle, bg: 'rgba(245,158,11,0.15)', border: '#F59E0B', text: '#FBBF24', glow: '0 0 28px rgba(245,158,11,0.35)' };
  const BannerIcon = banner.icon;

  return (
    <div
      className="h-screen w-screen overflow-hidden text-[#F5F3EF] p-4 lg:p-6 flex flex-col gap-4 font-['Outfit']"
      style={{
        background: `radial-gradient(circle at 50% 20%, rgba(26,61,44,0.25) 0%, rgba(6,18,13,0.97) 60%), url(${PINE_BG})`,
        backgroundSize: 'cover', backgroundPosition: 'center', backgroundBlendMode: 'multiply',
      }}
      data-testid="tree-tv-dashboard"
    >
      {/* Header */}
      <div className={`flex flex-wrap items-center justify-between p-4 lg:px-6 ${CARD}`}>
        <img
          src={WC_LOGO}
          alt="Wilson Contractors"
          className="h-14 object-contain"
          style={{ filter: 'drop-shadow(0 0 14px rgba(255,200,80,0.55)) drop-shadow(0 0 32px rgba(255,200,80,0.35)) brightness(1.08)' }}
        />
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2" data-testid="tv-sync-heartbeat">
            <span className="relative flex h-2.5 w-2.5">
              <span className={`absolute inline-flex h-full w-full rounded-full ${stale ? 'bg-red-500' : 'bg-emerald-400'} ${pulse ? 'animate-ping' : ''} opacity-75`} />
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${stale ? 'bg-red-500' : 'bg-emerald-400'}`} />
            </span>
            <span className="text-xs font-bold uppercase tracking-widest text-[#8AA193]">{stale ? 'Connection Lost' : 'Live Sync'}</span>
          </div>
          <div className="text-right">
            <p className="text-4xl lg:text-5xl font-extrabold tracking-tight tabular-nums text-[#F5F3EF]" data-testid="tv-live-clock">
              {time.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </p>
            <p className="text-sm text-[#8AA193] font-medium">
              {time.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>
      </div>

      {/* Stock status hero banner */}
      <div
        className="w-full rounded-2xl p-4 lg:p-5 flex items-center justify-between border shadow-lg transition-all duration-500"
        style={{ backgroundColor: banner.bg, borderColor: banner.border, boxShadow: banner.glow }}
        data-testid="tv-stock-status-banner"
      >
        <div className="flex items-center gap-3">
          <BannerIcon className="w-7 h-7 lg:w-8 lg:h-8" style={{ color: banner.text }} />
          <span className="text-xl lg:text-2xl font-extrabold tracking-tight" style={{ color: banner.text }}>{banner.label}</span>
        </div>
        <p className="text-sm lg:text-base text-[#A2B7A9]">{banner.sub}</p>
      </div>

      <div className="grid grid-cols-12 gap-4 lg:gap-5 flex-1 min-h-0 overflow-hidden">
        {/* Left column: Current Stock */}
        <div className="col-span-12 lg:col-span-4 flex flex-col gap-4 lg:gap-5 min-h-0">
          <div className={`p-5 flex-1 min-h-0 overflow-y-auto ${CARD}`} data-testid="tv-current-stock-grid">
            <h3 className="text-xs uppercase tracking-[0.2em] text-[#8AA193] font-bold mb-4 flex items-center gap-2"><TreePine className="w-3.5 h-3.5" /> Current Stock</h3>
            <div className="space-y-3">
              {colours.map(c => {
                const needed = totalRequired[c.color] || 0;
                const shortfall = needed > 0 ? Math.max(0, needed - c.quantity) : 0;
                const ok = shortfall === 0;
                return (
                  <div key={c.id} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center" style={pillStyle(c.color)}>
                      <div className="w-2.5 h-2.5 rounded-full bg-white/90" />
                    </div>
                    <span className="text-sm font-semibold capitalize flex-1 text-[#E1E8E3]">{c.label || c.color}</span>
                    <div className="text-right flex items-center gap-2 justify-end">
                      <div>
                        <span className="text-2xl font-extrabold tabular-nums" style={{ color: c.quantity < 0 ? '#F87171' : '#F5F3EF' }}>{c.quantity}</span>
                        {c.quantity < 0 && <AlertTriangle className="w-3.5 h-3.5 inline-block ml-1.5 mb-1 text-red-400" />}
                        {needed > 0 && ok && (
                          <span className="text-xs ml-2 font-semibold text-[#4ADE80]">covers {needed} needed</span>
                        )}
                      </div>
                      {shortfall > 0 && (
                        <span
                          className="text-[11px] font-extrabold px-2.5 py-1 rounded-full flex items-center gap-1 flex-shrink-0 animate-pulse"
                          style={{ backgroundColor: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.5)', color: '#F87171' }}
                          data-testid={`tv-shortfall-badge-${c.color}`}
                        >
                          <AlertTriangle className="w-3 h-3" /> +{shortfall} NEEDED
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-[#2E7553]/25 flex items-center justify-between">
              <span className="text-sm text-[#8AA193]">Total</span>
              <div>
                <span className="text-3xl font-extrabold" style={{ color: totalStock < 0 ? '#F87171' : '#F5F3EF' }}>{totalStock}</span>
                <span className="text-sm text-[#688272] ml-2">pallets</span>
              </div>
            </div>
            <p className="text-xs mt-1 text-right" style={{ color: totalStock < 0 ? '#F87171' : '#688272' }}>{(totalStock * settings.trees_per_pallet).toLocaleString()} trees</p>
          </div>
        </div>

        {/* Center column: Upcoming Collections */}
        <div className="col-span-12 lg:col-span-5 flex flex-col gap-4 lg:gap-5 min-h-0">
          <div className={`p-5 flex-1 min-h-0 overflow-hidden flex flex-col ${CARD}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs uppercase tracking-[0.2em] text-[#8AA193] font-bold flex items-center gap-2"><Truck className="w-3.5 h-3.5" /> Upcoming Collections</h3>
              <span className="text-sm text-[#8AA193] font-medium">{upcoming.length} scheduled</span>
            </div>
            <div className="space-y-3 overflow-y-auto flex-1" data-testid="tv-upcoming-collections-list">
              {upcoming.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-[#425148]">
                  <Truck className="w-12 h-12 mb-3" />
                  <p className="text-lg">No collections scheduled</p>
                </div>
              ) : (
                upcoming.map(load => {
                  const pallets = load.pallets_required || [];
                  const canFulfill = pallets.every(p => getStock(p.color) >= p.quantity);
                  const dateLabel = fmtDate(load.departure_date);
                  const isToday = dateLabel === 'TODAY';
                  const isTomorrow = dateLabel === 'TOMORROW';

                  return (
                    <div
                      key={load.id}
                      className="rounded-xl p-4 border transition-all"
                      style={isToday
                        ? { backgroundColor: 'rgba(245,158,11,0.12)', borderColor: 'rgba(245,158,11,0.4)', boxShadow: '0 0 16px rgba(245,158,11,0.15)' }
                        : { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(46,117,83,0.2)' }}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`rounded-lg px-3 py-2 text-center flex-shrink-0 min-w-[74px] ${isToday ? 'bg-amber-500 text-black' : isTomorrow ? 'bg-white/15' : 'bg-white/5'}`}>
                          <p className={`text-xs font-extrabold ${isToday ? 'text-black' : 'text-[#A2B7A9]'}`}>{dateLabel}</p>
                        </div>

                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={canFulfill ? { backgroundColor: 'rgba(34,197,94,0.2)' } : { backgroundColor: 'rgba(239,68,68,0.2)' }}>
                          {canFulfill ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-base truncate text-[#F5F3EF]">{load.driver_name || '—'}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {load.destinations?.map((d, i) => (
                              <span key={i} className="text-xs text-[#8AA193] flex items-center gap-1 truncate">
                                <MapPin className="w-3 h-3 text-[#E27B58]" /> {d.address}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="flex gap-2 flex-shrink-0">
                          {pallets.map((p, i) => {
                            const ok = getStock(p.color) >= p.quantity;
                            return (
                              <div
                                key={i}
                                className="rounded-lg px-3 py-1.5 text-center border"
                                style={{ backgroundColor: ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', borderColor: ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)' }}
                              >
                                <p className="text-xl font-extrabold tabular-nums text-[#F5F3EF]">{p.quantity}</p>
                                <p className="text-[10px] capitalize font-semibold" style={{ color: colourHex(p.color) }}>{labelFor(p.color)}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Right column: KPIs, Haulers, Demand */}
        <div className="col-span-12 lg:col-span-3 flex flex-col gap-4 lg:gap-5 min-h-0 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3" data-testid="tv-kpi-stats-grid">
            <div className={`p-4 ${CARD}`}>
              <TreePine className="w-4 h-4 text-emerald-400 mb-2" />
              <p className="text-2xl lg:text-3xl font-extrabold tabular-nums text-[#F5F3EF]">{analytics?.total_out || 0}</p>
              <p className="text-[10px] text-[#688272] uppercase tracking-wider font-semibold">Dispatched</p>
            </div>
            <div className={`p-4 ${CARD}`}>
              <Clock className="w-4 h-4 text-blue-400 mb-2" />
              <p className="text-2xl lg:text-3xl font-extrabold tabular-nums text-[#F5F3EF]">{analytics?.avg_yard_days || 0}</p>
              <p className="text-[10px] text-[#688272] uppercase tracking-wider font-semibold">Avg Days</p>
            </div>
            <div className={`p-4 ${CARD}`}>
              <Truck className="w-4 h-4 text-[#E27B58] mb-2" />
              <p className="text-2xl lg:text-3xl font-extrabold tabular-nums text-[#F5F3EF]">{upcoming.length}</p>
              <p className="text-[10px] text-[#688272] uppercase tracking-wider font-semibold">Upcoming</p>
            </div>
            <div className={`p-4 ${CARD}`}>
              <Package className="w-4 h-4 text-yellow-400 mb-2" />
              <p className="text-2xl lg:text-3xl font-extrabold tabular-nums" style={{ color: totalStock < 0 ? '#F87171' : '#F5F3EF' }}>{(totalStock * settings.trees_per_pallet).toLocaleString()}</p>
              <p className="text-[10px] text-[#688272] uppercase tracking-wider font-semibold">Trees</p>
            </div>
          </div>

          <div className={`p-5 ${CARD}`} data-testid="tv-top-haulers-chart">
            <h3 className="text-xs uppercase tracking-[0.2em] text-[#8AA193] font-bold mb-4">Top Haulers</h3>
            <div className="space-y-3">
              {(analytics?.hauler_stats || []).slice(0, 6).map((h, i) => {
                const max = Math.max(...(analytics?.hauler_stats || []).map(x => x.loads));
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-[#A2B7A9] truncate max-w-[140px] font-medium">{h.hauler}</span>
                      <span className="text-xs font-bold tabular-nums text-[#F5F3EF]">{h.loads} loads</span>
                    </div>
                    <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${max > 0 ? (h.loads / max) * 100 : 0}%`, background: 'linear-gradient(90deg,#E27B58,#F59E0B)', boxShadow: '0 0 8px rgba(226,123,88,0.5)' }} />
                    </div>
                  </div>
                );
              })}
              {!analytics?.hauler_stats?.length && (
                <p className="text-sm text-[#425148] text-center py-4">No hauler data</p>
              )}
            </div>
          </div>

          <div className={`p-5 ${CARD}`} data-testid="tv-demand-breakdown">
            <h3 className="text-xs uppercase tracking-[0.2em] text-[#8AA193] font-bold mb-3">Demand</h3>
            <div className="flex gap-3">
              {(analytics?.colour_demand || []).slice(0, 4).map((c, i) => {
                return (
                  <div key={i} className="flex-1 text-center">
                    <div className="w-7 h-7 rounded-full mx-auto mb-1" style={pillGlow(c.color)} />
                    <p className="text-lg font-extrabold tabular-nums text-[#F5F3EF]">{c.total}</p>
                    <p className="text-[9px] text-[#8AA193] capitalize font-medium">{labelFor(c.color)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Just dispatched ticker */}
      {tickerItems.length > 0 && (
        <div className={`flex items-center overflow-hidden ${CARD}`} data-testid="tv-dispatch-ticker">
          <div className="flex items-center gap-2 px-4 py-2.5 flex-shrink-0 bg-[#1A3D2C] border-r border-[#2E7553]/30 rounded-l-2xl">
            <Truck className="w-4 h-4 text-amber-400" />
            <span className="text-xs font-extrabold uppercase tracking-widest text-amber-400 whitespace-nowrap">Just Dispatched</span>
          </div>
          <div className="relative flex-1 overflow-hidden h-9">
            <div
              className="absolute inset-y-0 left-0 flex items-center gap-10 whitespace-nowrap tv-ticker-track"
              style={{ animationDuration: `${Math.max(20, tickerItems.length * 7)}s` }}
              data-testid="tv-dispatch-ticker-track"
            >
              {[...tickerItems, ...tickerItems].map((item, i) => (
                <span key={i} className="text-sm text-[#D6E2DA] font-medium flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                  {item.text}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-[#4A5D53] text-[10px] px-1">
        <span className="flex items-center gap-1.5"><Radio className="w-3 h-3" /> Tree Management System</span>
        <span>Mains of Corsindae, Sauchen, AB51 7PN</span>
        <span>Auto-refreshes every 30s</span>
      </div>
      <style>{`
        @keyframes tv-ticker-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .tv-ticker-track {
          animation-name: tv-ticker-scroll;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          padding-left: 1.5rem;
        }
      `}</style>
    </div>
  );
}
